import { useLingui } from "@lingui/react/macro";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle } from "lucide-react";
import {
  ApiDatatable,
  type ApiDatatableBaseParams,
} from "@/components/data-table";
import { locationApi } from "@/lib/api/admin-api";
import { companyApi } from "@/lib/api/services/company.api";
import { useAuth } from "@/hooks/useAuth";
import { useRouter } from "@tanstack/react-router";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ListSectionHeader } from "@/components/common/ListSectionHeader";
import { LocationTile } from "./LocationTile";
import { CreateLocationDialog } from "./CreateLocationDialog";
import { getLocationColumns } from "./columns";

export interface LocationListingSearchParams extends ApiDatatableBaseParams {
  status: "all" | "active" | "inactive";
}

interface LocationListingProps {
  searchParams: LocationListingSearchParams;
  onNavigate: (
    updater: (prev: LocationListingSearchParams) => LocationListingSearchParams,
  ) => void;
  /** When set, filters to locations belonging to this region. */
  regionId?: number;
}

export function LocationListing({
  searchParams,
  onNavigate,
  regionId,
}: LocationListingProps) {
  const { t, i18n } = useLingui();
  const { isAuthenticated, hasRole } = useAuth();
  const router = useRouter();
  const isSuperAdmin = hasRole("SUPER_ADMIN");
  const [total, setTotal] = useState<number>();

  // The org-level section header only belongs to the standalone Locations tab.
  // When rendered inside a region's detail page (regionId set), the parent owns the header.
  const showSectionHeader = regionId === undefined;

  const { data: companiesWithOrganization = [] } = useQuery({
    queryKey: ["companies", "for-locations-organization-column"],
    enabled: isAuthenticated && isSuperAdmin,
    queryFn: async () => {
      const pageSize = 100;
      let page = 1;
      let totalPages = 1;
      const companies: Array<{
        id: number;
        organizationId: number;
        organization?: { name: string };
      }> = [];

      while (page <= totalPages) {
        const response = await companyApi.paginate({ page, limit: pageSize });
        companies.push(...response.data);

        const total = response.meta?.total ?? response.data.length;
        totalPages = Math.max(1, Math.ceil(total / pageSize));
        page += 1;
      }

      return companies;
    },
  });

  const organizationNameByCompanyId = useMemo(() => {
    const map = new Map<number, string>();
    companiesWithOrganization.forEach((company) => {
      if (company.organization?.name) {
        map.set(company.id, company.organization.name);
      }
    });
    return map;
  }, [companiesWithOrganization]);

  const getOrganizationName = (companyId: number) =>
    organizationNameByCompanyId.get(companyId) ?? "—";

  const columns = getLocationColumns(
    i18n,
    (locationId) =>
      void router.navigate({
        to: "/locations/$locationId",
        params: { locationId: String(locationId) },
      }),
    isSuperAdmin
      ? (location) => getOrganizationName(location.companyId)
      : undefined,
  );

  return (
    <div className="flex flex-col gap-3">
      {showSectionHeader && (
        <ListSectionHeader title={t`Locations`} count={total}>
          <Button
            variant="outline"
            className="h-10 gap-2 rounded-[10px] border-(--shell-border) text-sm font-medium text-(--color-text-primary)"
          >
            <CheckCircle className="size-4" />
            {t`Select`}
          </Button>
          <CreateLocationDialog />
        </ListSectionHeader>
      )}
      <ApiDatatable
        searchParams={searchParams}
        onNavigate={onNavigate}
        onTotalChange={setTotal}
        queryKey={["locations", regionId]}
        queryFn={(params) =>
          locationApi.paginate({
            regionId,
            search: params.search,
            page: params.page,
            limit: params.pageSize,
            isActive:
              params.status === "active"
                ? true
                : params.status === "inactive"
                  ? false
                  : undefined,
          })
        }
        columns={columns}
        renderTile={(location) => (
          <LocationTile
            location={location}
            organizationName={
              isSuperAdmin ? getOrganizationName(location.companyId) : undefined
            }
          />
        )}
        getRowId={(location) => String(location.id)}
        onRowClick={(location) =>
          void router.navigate({
            to: "/locations/$locationId",
            params: { locationId: String(location.id) },
          })
        }
        enabled={isAuthenticated}
        searchPlaceholder={t`Search locations…`}
        emptyMessage={t`No locations found`}
        toolbarExtra={
          <Select
            value={searchParams.status}
            onValueChange={(status) =>
              onNavigate((prev) => ({
                ...prev,
                status: status as "all" | "active" | "inactive",
                page: 1,
              }))
            }
          >
            <SelectTrigger className="w-35">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t`All`}</SelectItem>
              <SelectItem value="active">{t`Active`}</SelectItem>
              <SelectItem value="inactive">{t`Inactive`}</SelectItem>
            </SelectContent>
          </Select>
        }
      />
    </div>
  );
}
